/* ═══════════════════════════════════════════════════════════════
   Add Specs — Pull master specs into a project (formerly "Compile")
   One screen: pick project → pick sections (+ variant) → Add.
   Forma lives in the header; smart blocks resolve on Add.
   Adding snapshots each chosen master into a per-project spec.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import { Zap, X, Check, FilePenLine } from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect';
import { DataTable, type Column } from '../components/DataTable';
import { isWeb } from '../lib/platform';
import { ResolvedBlocks } from '../components/smart-blocks/SmartBlockBuilder';
import { Button } from '../components/ui/button';
import { SpecService } from '../services/specService';
import { ProjectService } from '../services/projectService';
import { ProjectSpecService } from '../services/projectSpecService';
import { FormaService } from '../services/formaService';
import { OrgService } from '../services/orgService';
import { TeamService } from '../services/teamService';
import { SmartBlockService } from '../services/smartBlockService';
import {
  buildProjectContext,
  resolveSmartToken,
  findSmartBlockInCatalog,
} from '../../engine/smart-blocks';
import type {
  Project,
  SpecMaster,
  StandardConfig,
  ProjectSpec,
  Variant,
  Organization,
  ContentBlock,
  SmartBlock,
} from '../../shared/types';

interface AddSpecsProps {
  toast: (msg: string, isError?: boolean) => void;
  standards: StandardConfig[];
  /** Deep-link in (e.g. "+ Add Specs" from a project dashboard) → open
      straight into that project's section picker. Add Specs is project-first:
      it's only reachable from a project, never as a standalone destination. */
  deepLink?: { view: 'compile'; projectId?: string } | null;
  onDeepLinkConsumed?: () => void;
  /** Leave the cockpit — back to the project (or the Projects hub). */
  onExit?: (projectId?: string) => void;
  /** After adding, jump to the project's dashboard (detail view). */
  onOpenProjectDashboard?: (projectId: string) => void;
}

interface MasterSelection {
  masterId: string;
  variantId?: string;
}

/** One section's smart-block resolutions for the pre-add preview. */
interface PreviewSection {
  master: SpecMaster;
  variantName?: string;
  tokens: Array<{
    block: ContentBlock;
    sb: SmartBlock | undefined;
    resolved: ContentBlock[];
  }>;
}

/** Smart-block brand accent — matches SmartBlockBuilder / SmartTokenResolvedView.
    Backed by the shared --color-smart token so the cart preview stays
    color-matched with the shared smart-block components rendered inside it. */
const SMART_ACCENT = 'var(--color-smart)';

/** Shared micro-interaction transition for hand-rolled toggles/pills. */
const FAST_COLORS =
  'background-color var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out), border-color var(--duration-fast) var(--ease-out)';

/**
 * Flatten a master into the blocks a project spec would receive for the
 * chosen variant: core blocks the variant hasn't excluded + blocks owned by
 * that variant, with all variant markers stripped. Single source of truth —
 * used by both the resolution preview and the actual add.
 */
function flattenMaster(master: SpecMaster, variantId: string | undefined): ContentBlock[] {
  const masterBlocks: any[] = JSON.parse(JSON.stringify(master.coreContent || []));
  return masterBlocks
    .filter((b: any) => {
      if (b.isVariant) {
        return !!variantId && b.variantId === variantId;
      }
      const excluded: string[] = b.excludedFromVariants || [];
      return !variantId || !excluded.includes(variantId);
    })
    .map((b: any) => {
      const {
        isVariant: _iv,
        variantIds: _vids,
        variantId: _vid,
        excludedFromVariants: _ex,
        ...rest
      } = b;
      return rest;
    });
}

function AddSpecs({
  toast,
  standards,
  deepLink,
  onDeepLinkConsumed,
  onExit,
  onOpenProjectDashboard,
}: AddSpecsProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [masters, setMasters] = useState<SpecMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Active project (set by the deep-link from a project dashboard)
  const [activeProject, setActiveProject] = useState<Project | null>(null);

  // Selection
  const [selectedMasters, setSelectedMasters] = useState<MasterSelection[]>([]);
  const [selectedFormaId, setSelectedFormaId] = useState('');

  // Existing project specs (if project was already populated before)
  const [existingSpecs, setExistingSpecs] = useState<ProjectSpec[]>([]);
  const [adding, setAdding] = useState(false);

  // Live smart-block resolution — context hydrated once per project, then
  // resolved inline in the cart as the selection changes. No modal: the PM
  // watches the smart blocks fill in from the project as they pick sections.
  const [resolveCtx, setResolveCtx] = useState<{
    catalog: SmartBlock[];
    team: Array<{ id: string; name?: string }>;
    clientOrg: Organization | undefined;
  } | null>(null);

  // Division quick-filter (left of the table; null = all divisions)
  const [divFilter, setDivFilter] = useState<string | null>(null);

  // Rename tool
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameFolder, setRenameFolder] = useState('');
  const [renameFormat, setRenameFormat] = useState('## ## ##');
  const [renamePreview, setRenamePreview] = useState<
    Array<{ original: string; renamed: string; error?: string }>
  >([]);
  const [renameRunning, setRenameRunning] = useState(false);
  const [renameDone, setRenameDone] = useState(false);

  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);

  // Esc closes the rename modal (a11y — overlays dismiss on Escape).
  useEffect(() => {
    if (!showRenameModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowRenameModal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showRenameModal]);

  // Deep-link in: "+ Add Specs" from a project dashboard opens straight into
  // that project's picker. Wait for projects to load, match by id, consume once.
  useEffect(() => {
    if (!deepLink || deepLink.view !== 'compile' || !deepLink.projectId) return;
    if (projects.length === 0) return;
    const proj = projects.find((p) => p.id === deepLink.projectId);
    if (proj) selectProject(proj);
    onDeepLinkConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink, projects]);

  // Hydrate the resolution context once when a project becomes active — the
  // same sources BlockEditor uses (firm smart-block catalog, assigned team,
  // client org). Cached in state so live resolution stays synchronous.
  useEffect(() => {
    if (!activeProject) {
      setResolveCtx(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const [firmCatalog, assignments, allMembers] = await Promise.all([
        SmartBlockService.list().catch(() => [] as SmartBlock[]),
        TeamService.listAssignments(undefined, activeProject.id).catch(() => [] as any[]),
        TeamService.list().catch(() => [] as any[]),
      ]);
      if (cancelled) return;
      const memberById = new Map((allMembers as any[]).map((m: any) => [m.id, m]));
      const team = (assignments as any[])
        .map((a: any) => {
          const m: any = memberById.get(a.teamMemberId);
          if (!m) return null;
          return {
            id: m.id,
            name: m.name || m.fullName || m.displayName,
            email: m.email,
            role: a.role || m.role,
            title: m.title,
          };
        })
        .filter(Boolean) as Array<{ id: string; name?: string }>;
      const clientOrgId = (activeProject as any).clientOrgId || (activeProject as any).clientId;
      const clientOrg = clientOrgId ? orgs.find((o) => o.id === clientOrgId) : undefined;
      setResolveCtx({ catalog: firmCatalog as SmartBlock[], team, clientOrg });
    })();
    return () => {
      cancelled = true;
    };
  }, [activeProject, orgs]);

  async function loadData() {
    // Settled, not all-or-nothing — whatever loads still renders (DESIGN §5).
    const [p, m, o] = await Promise.allSettled([
      ProjectService.list(),
      SpecService.listMasters(),
      OrgService.list(),
    ]);
    if (p.status === 'fulfilled') setProjects(p.value);
    if (m.status === 'fulfilled') setMasters(m.value);
    if (o.status === 'fulfilled') setOrgs(o.value);
    const failed = [p, m, o].some((r) => r.status === 'rejected');
    setLoadError(
      failed
        ? "Some data didn't load. Check your connection — it reloads automatically when sync completes."
        : null
    );
    setLoading(false);
  }

  // ─── Pick or create project ───
  function selectProject(proj: Project) {
    const resolved = JSON.parse(JSON.stringify(proj));
    if (!resolved.clientName && resolved.clientOrgId) {
      resolved.clientName = orgs.find((o) => o.id === resolved.clientOrgId)?.name;
    }
    setActiveProject(resolved);
    // Auto-select ONLY a forma explicitly applied to this project: an
    // attached master, or the source master of the project's forma copy.
    // Anything else (including client-name matches) requires the user to
    // pick — firms keep several formas per client, silent guesses pick wrong.
    const attachedForma = standards.find(
      (s: any) => s.attachedProjectId === resolved.id && !s.projectId
    );
    if (attachedForma) {
      setSelectedFormaId(attachedForma.id);
    } else {
      FormaService.list()
        .then((all: any[]) => {
          const copy = all.find((f: any) => f.projectId === resolved.id && !f.specId);
          const srcId = copy?.sourceFormaId;
          setSelectedFormaId(srcId && standards.some((s: any) => s.id === srcId) ? srcId : '');
        })
        .catch(() => setSelectedFormaId(''));
    }
    // Load existing project specs and pre-select what's already there
    ProjectSpecService.listByProject(proj.id)
      .then((specs) => {
        setExistingSpecs(specs);
        setSelectedMasters(
          specs.length > 0
            ? specs.map((s) => ({ masterId: s.masterId, variantId: s.variantId }))
            : []
        );
      })
      .catch(() => setExistingSpecs([]));
  }

  // ─── Selection helpers ───
  function toggleMaster(masterId: string) {
    setSelectedMasters((prev) => {
      const exists = prev.find((s) => s.masterId === masterId);
      if (exists) return prev.filter((s) => s.masterId !== masterId);
      return [...prev, { masterId }];
    });
  }

  function setVariant(masterId: string, variantId: string | undefined) {
    setSelectedMasters((prev) =>
      prev.map((s) => (s.masterId === masterId ? { ...s, variantId } : s))
    );
  }

  function isSelected(masterId: string) {
    return selectedMasters.some((s) => s.masterId === masterId);
  }

  // ─── Add to project (snapshot each master into a project spec) ───
  async function addToProject() {
    if (!activeProject || selectedMasters.length === 0) return;
    setAdding(true);
    const now = new Date().toISOString();

    try {
      // Specs-style forma application: a MASTER forma picked here is applied
      // to the project as a COPY (idempotent — reuses the project's existing
      // copy of the same master). New specs bind to the copy, so edits inside
      // the project never touch the master.
      let effectiveFormaId = selectedFormaId || undefined;
      const pickedMaster = standards.find((s: any) => s.id === selectedFormaId && !s.projectId);
      if (pickedMaster) {
        try {
          const copy = await FormaService.ensureProjectCopy(pickedMaster, {
            id: activeProject.id,
            number: (activeProject as any).projectNumber,
            name: activeProject.name,
          });
          effectiveFormaId = copy.id;
        } catch (err) {
          console.warn('[add-specs] project forma copy failed, binding master:', err);
        }
      }
      for (const sel of selectedMasters) {
        const master = masters.find((m) => m.id === sel.masterId);
        if (!master) continue;

        // Already in the project? Update the variant choice, preserve project edits.
        const existing = existingSpecs.find((s) => s.masterId === sel.masterId);
        if (existing) {
          if (existing.variantId !== sel.variantId) {
            existing.variantId = sel.variantId;
            existing.lastEdited = now;
            await ProjectSpecService.save(existing);
          }
          continue;
        }

        // New project spec (fork from master). Project specs are a FLATTENED
        // snapshot of the chosen variant — once added, there's no All/Core/V1
        // concept. flattenMaster keeps core blocks the variant hasn't excluded
        // + variant blocks matching the selection; variant markers stripped.
        const flatBlocks = flattenMaster(master, sel.variantId);

        const projectSpec: ProjectSpec = {
          id: 'ps-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 6),
          projectId: activeProject.id,
          masterId: master.id,
          masterVersion: master.version,
          variantId: sel.variantId,
          formaId: effectiveFormaId,
          csiNumber: master.csiNumber,
          title: master.title,
          description: master.description,
          coreContent: flatBlocks,
          systems: JSON.parse(JSON.stringify(master.systems || [])),
          variants: [],
          compiledAt: now,
          lastEdited: now,
        };

        await ProjectSpecService.save(projectSpec);
      }

      // Remove project specs for masters that were deselected
      for (const existing of existingSpecs) {
        if (!selectedMasters.find((s) => s.masterId === existing.masterId)) {
          await ProjectSpecService.delete(existing.id);
        }
      }

      // Update project record with master list
      activeProject.masters = selectedMasters.map((s) => s.masterId);
      activeProject.updatedAt = new Date().toISOString();
      await ProjectService.save(activeProject);

      toast('Specs added to project');
      const updatedSpecs = await ProjectSpecService.listByProject(activeProject.id);
      setExistingSpecs(updatedSpecs);
      await loadData();

      if (onOpenProjectDashboard) onOpenProjectDashboard(activeProject.id);
    } catch (err: any) {
      toast('Failed to add specs: ' + (err?.message || err), true);
    } finally {
      setAdding(false);
    }
  }

  // ─── Live smart-block resolution ───
  // Resolves every smart-token block in the selected sections against the
  // POST-ADD project context (all selected sections see each other), using
  // the same engine the editor and Word export use. Recomputes as the
  // selection changes so the cart shows exactly what will land — live.
  const liveSections = useMemo<PreviewSection[]>(() => {
    if (!activeProject || !resolveCtx || selectedMasters.length === 0) return [];

    const postAddSpecs = selectedMasters
      .map((sel) => {
        const m = masters.find((x) => x.id === sel.masterId);
        if (!m) return null;
        const existing = existingSpecs.find((s) => s.masterId === sel.masterId);
        return {
          id: existing?.id || 'preview-' + m.id,
          csiNumber: m.csiNumber,
          title: m.title,
        } as ProjectSpec;
      })
      .filter(Boolean) as ProjectSpec[];

    const projectCtx = {
      id: activeProject.id,
      name: activeProject.name,
      number: (activeProject as any).projectNumber,
      address: (activeProject as any).address,
      phase: (activeProject as any).currentPhaseId,
      clientId: (activeProject as any).clientOrgId || (activeProject as any).clientId,
    };

    const sections: PreviewSection[] = [];
    for (const sel of selectedMasters) {
      const master = masters.find((m) => m.id === sel.masterId);
      if (!master) continue;
      const flat = flattenMaster(master, sel.variantId);
      const tokenBlocks = flat.filter((b: any) => b.blockType === 'smart-token');
      if (tokenBlocks.length === 0) continue;

      const existing = existingSpecs.find((s) => s.masterId === sel.masterId);
      const ctx = buildProjectContext({
        project: projectCtx,
        thisSpec: {
          id: existing?.id || 'preview-' + master.id,
          masterId: master.id,
          csiNumber: master.csiNumber,
          sectionNumber: master.csiNumber,
          title: master.title,
          coreContent: flat,
        },
        allProjectSpecs: postAddSpecs,
        team: resolveCtx.team,
        client: resolveCtx.clientOrg
          ? {
              id: resolveCtx.clientOrg.id,
              name: resolveCtx.clientOrg.name,
              address: (resolveCtx.clientOrg as any).address,
            }
          : undefined,
      });

      sections.push({
        master,
        variantName: sel.variantId
          ? (master.variants || []).find((v) => v.id === sel.variantId)?.name
          : undefined,
        tokens: tokenBlocks.map((block) => {
          const sb = findSmartBlockInCatalog((block as any).smartBlockId, resolveCtx.catalog);
          let resolved: ContentBlock[] = [];
          try {
            resolved = resolveSmartToken(block, sb, ctx);
          } catch {
            resolved = [];
          }
          return { block, sb, resolved };
        }),
      });
    }
    return sections;
  }, [activeProject, resolveCtx, selectedMasters, masters, existingSpecs]);

  // ─── Rename spec files tool ───
  async function pickRenameFolder() {
    const folder = await window.api.selectFolderDialog();
    if (!folder) return;
    setRenameFolder(folder);
    setRenameDone(false);
    setRenamePreview([]);
  }

  async function executeRename() {
    if (!renameFolder) return;
    setRenameRunning(true);
    try {
      const results = await window.api.renameSpecFiles(renameFolder, renameFormat);
      setRenamePreview(results);
      setRenameDone(true);
      const renamed = results.filter((r) => r.original !== r.renamed && !r.error).length;
      const errors = results.filter((r) => r.error).length;
      if (renamed > 0)
        toast(
          `Renamed ${renamed} file${renamed !== 1 ? 's' : ''}${errors > 0 ? ` (${errors} error${errors !== 1 ? 's' : ''})` : ''}`
        );
      else if (errors > 0)
        toast(`${errors} error${errors !== 1 ? 's' : ''} — no files renamed`, true);
      else toast('All files already match the target format');
    } catch (err: any) {
      toast('Rename failed: ' + err.message, true);
    }
    setRenameRunning(false);
  }

  // ─── Derived: live counts ───
  // Real smart-block count — counts smart-token blocks on the FLATTENED
  // (variant-filtered) blocks, so tokens excluded by the chosen variant
  // don't inflate the number.
  const smartBlockCount = useMemo(
    () =>
      selectedMasters.reduce((n, sel) => {
        const m = masters.find((x) => x.id === sel.masterId);
        if (!m) return n;
        return (
          n +
          flattenMaster(m, sel.variantId).filter((b: any) => b.blockType === 'smart-token').length
        );
      }, 0),
    [selectedMasters, masters]
  );

  const newCount = useMemo(
    () =>
      selectedMasters.filter((s) => !existingSpecs.some((e) => e.masterId === s.masterId)).length,
    [selectedMasters, existingSpecs]
  );

  // ─── CSI divisions (quick-filter rail above the table) ───
  const divisions = useMemo(() => {
    const map = new Map<string, number>();
    masters.forEach((m) => {
      const d = (m.csiNumber || '').split(' ')[0];
      if (d) map.set(d, (map.get(d) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [masters]);

  const shownMasters = useMemo(
    () =>
      divFilter ? masters.filter((m) => (m.csiNumber || '').split(' ')[0] === divFilter) : masters,
    [masters, divFilter]
  );

  // ─── Rename Modal ───
  const renameModal = (
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setShowRenameModal(false);
      }}
      style={overlayStyle}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Rename spec files"
        style={modalStyle}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 'var(--space-1)' }}>
          Rename Spec Files
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--tm)',
            marginBottom: 'var(--space-4)',
            lineHeight: 1.5,
          }}
        >
          Select a folder of .docx/.pdf spec files and rename them to match a spec number format.
          Files must follow the pattern:{' '}
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
            [digits] - [name].docx
          </span>
        </div>

        {/* Folder picker */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--tm)',
              marginBottom: 'var(--space-1)',
            }}
          >
            Folder
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <div
              style={{
                flex: 1,
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                color: renameFolder ? 'var(--tx)' : 'var(--tm)',
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {renameFolder || 'No folder selected...'}
              </span>
            </div>
            <Button variant="outline" onClick={pickRenameFolder}>
              Browse
            </Button>
          </div>
        </div>

        {/* Format picker */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--tm)',
              marginBottom: 'var(--space-1)',
            }}
          >
            Target Format
          </div>
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
              marginBottom: 'var(--space-2)',
            }}
          >
            {['## ## ##', '## ####', '######', '##-##-##', '##-####'].map((fmt) => (
              <button
                key={fmt}
                onClick={() => setRenameFormat(fmt)}
                aria-pressed={renameFormat === fmt}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-sm)',
                  border: renameFormat === fmt ? '1px solid var(--ac)' : '1px solid var(--bd)',
                  background: renameFormat === fmt ? 'rgba(var(--acr),0.08)' : 'transparent',
                  color: renameFormat === fmt ? 'var(--ac)' : 'var(--tm)',
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: 'var(--font-mono)',
                  cursor: 'pointer',
                  transition: FAST_COLORS,
                }}
              >
                {fmt}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <label htmlFor="rename-custom-format" style={{ fontSize: 11, color: 'var(--tm)' }}>
              Custom:
            </label>
            <input
              id="rename-custom-format"
              value={renameFormat}
              onChange={(e) => setRenameFormat(e.target.value)}
              style={{
                width: 120,
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                color: 'var(--tx)',
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
              }}
            />
            <span style={{ fontSize: 11, color: 'var(--tm)' }}>Use # for digits</span>
          </div>
        </div>

        {/* Results */}
        {renamePreview.length > 0 && (
          <div
            style={{
              marginBottom: 'var(--space-4)',
              maxHeight: 200,
              overflow: 'auto',
              background: 'var(--bg)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--bd)',
            }}
          >
            {renamePreview.map((r, i) => (
              <div
                key={i}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderBottom: '1px solid var(--bd)',
                  fontSize: 11,
                  fontFamily: 'var(--font-mono)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                }}
              >
                {r.error ? (
                  <span style={{ color: 'var(--dg)' }}>
                    {r.original} — {r.error}
                  </span>
                ) : r.original === r.renamed ? (
                  <span style={{ color: 'var(--tm)' }}>{r.original} (no change)</span>
                ) : (
                  <>
                    <span style={{ color: 'var(--tm)', textDecoration: 'line-through' }}>
                      {r.original}
                    </span>
                    <span style={{ color: 'var(--ac)' }}>&rarr;</span>
                    <span style={{ color: 'var(--ok)' }}>{r.renamed}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="outline" onClick={() => setShowRenameModal(false)}>
            {renameDone ? 'Close' : 'Cancel'}
          </Button>
          {!renameDone && (
            <Button onClick={executeRename} disabled={!renameFolder || renameRunning}>
              {renameRunning ? 'Renaming...' : 'Rename Files'}
            </Button>
          )}
          {renameDone && renameFolder && (
            <Button variant="outline" onClick={() => window.api.openFolder(renameFolder)}>
              Open Folder
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  // No active project — Add Specs is project-first, reached only via the
  // "+ Add Specs" entry on a project dashboard. This state is transient
  // (the deep-link is resolving) or a stray landing; send them to Projects.
  // ═══════════════════════════════════════════════════════════════
  if (!activeProject) {
    const resolving = !!deepLink?.projectId;
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-4)',
          color: 'var(--tm)',
        }}
      >
        {resolving ? (
          <div style={{ fontSize: 13 }}>Opening project…</div>
        ) : (
          <>
            <div style={{ fontSize: 14 }}>Add specs to a project from its dashboard.</div>
            <Button onClick={() => onExit?.()}>Go to Projects</Button>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // SCREEN 2 — Add sections to the active project (single screen)
  // ═══════════════════════════════════════════════════════════════
  const formaOptions = standards.map((s) => ({
    value: s.id,
    label:
      (s.docSettings?.standardName || 'Untitled') +
      (s.docSettings?.clientName ? ` — ${s.docSettings.clientName}` : ''),
  }));

  const columns: Column<SpecMaster>[] = [
    {
      key: 'select',
      label: '',
      width: 44,
      minWidth: 44,
      sortable: false,
      render: (m) => {
        const sel = isSelected(m.id);
        return (
          <span
            role="checkbox"
            aria-checked={sel}
            aria-label={
              sel ? `Deselect ${m.csiNumber || m.title}` : `Select ${m.csiNumber || m.title}`
            }
            onClick={(e) => {
              e.stopPropagation();
              toggleMaster(m.id);
            }}
            style={{
              width: 18,
              height: 18,
              borderRadius: 'var(--radius-sm)',
              border: '2px solid ' + (sel ? 'var(--ac)' : 'var(--bd)'),
              background: sel ? 'var(--ac)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-inverse)',
              cursor: 'pointer',
              transition: FAST_COLORS,
            }}
          >
            {sel ? <Check size={12} strokeWidth={3} /> : null}
          </span>
        );
      },
    },
    {
      key: 'csiNumber',
      label: 'Section',
      width: 100,
      getValue: (m) => m.csiNumber || '',
      render: (m) => (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            color: 'var(--ac)',
            fontWeight: 700,
          }}
        >
          {m.csiNumber || '—'}
        </span>
      ),
    },
    {
      key: 'title',
      label: 'Title',
      width: 320,
      getValue: (m) => m.title || '',
      render: (m) => <span style={{ fontWeight: 500 }}>{m.title || 'Untitled'}</span>,
    },
    {
      key: 'variant',
      label: 'Variant',
      width: 200,
      sortable: false,
      render: (m) => {
        const sel = selectedMasters.find((s) => s.masterId === m.id);
        const hasVariants = (m.variants || []).length > 0;
        if (!sel) {
          return (
            <span style={{ fontSize: 11, color: 'var(--tm)' }}>
              {hasVariants ? `${(m.variants || []).length} variant(s)` : '—'}
            </span>
          );
        }
        if (!hasVariants) {
          return <span style={{ fontSize: 11, color: 'var(--tm)' }}>Core only</span>;
        }
        return (
          <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <SearchableSelect
              value={sel.variantId || ''}
              options={[
                { value: '', label: 'Core only' },
                ...(m.variants || []).map((v: Variant) => ({ value: v.id, label: v.name })),
              ]}
              onChange={(v) => setVariant(m.id, v || undefined)}
              clearable={false}
              searchPlaceholder="Variant…"
            />
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      sortable: false,
      render: (m) => {
        const sel = isSelected(m.id);
        const wasThere = existingSpecs.some((s) => s.masterId === m.id);
        if (!sel)
          return wasThere ? (
            <span style={badge('color-mix(in srgb, var(--dg) 10%, transparent)', 'var(--dg)')}>
              Will remove
            </span>
          ) : (
            <span style={{ fontSize: 11, color: 'var(--tm)' }}>—</span>
          );
        if (wasThere)
          return <span style={badge('rgba(var(--acr),0.1)', 'var(--ac)')}>In project</span>;
        return <span style={badge('rgba(var(--okr),0.1)', 'var(--ok)')}>Add</span>;
      },
    },
    {
      key: 'version',
      label: 'Ver',
      width: 64,
      getValue: (m) => m.version || 0,
      render: (m) => <span style={{ fontSize: 11, color: 'var(--tm)' }}>v{m.version}</span>,
    },
  ];

  const selectedForma = standards.find((s) => s.id === selectedFormaId);
  const clientName = activeProject.clientName;
  const formaIsClientMatch =
    !!clientName &&
    !!selectedForma?.docSettings?.clientName &&
    selectedForma.docSettings.clientName.toLowerCase() === clientName.toLowerCase();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* ── Header: just back + project identity (forma + Add live in the cart) ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          padding: 'var(--space-3) var(--space-5)',
          borderBottom: '1px solid var(--bd)',
          background: 'var(--sf)',
          flexShrink: 0,
        }}
      >
        <Button
          variant="outline"
          onClick={() => onExit?.(activeProject.id)}
          title="Back to the project dashboard"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </Button>

        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {activeProject.name || 'Untitled'}
          </div>
          {(activeProject.projectNumber || clientName) && (
            <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2 }}>
              {activeProject.projectNumber ? activeProject.projectNumber + ' · ' : ''}
              {clientName || ''}
            </div>
          )}
        </div>

        <div style={{ flex: 1 }} />

        {/* Rename Spec Files — batch file-naming utility (folder of .docx/.pdf).
            Desktop only: it works on a local folder. */}
        {!isWeb && (
          <Button
            variant="outline"
            onClick={() => {
              setShowRenameModal(true);
              setRenameDone(false);
              setRenamePreview([]);
              setRenameFolder('');
            }}
            title="Batch-rename a folder of spec files to a spec-number format"
          >
            <FilePenLine size={13} strokeWidth={2} aria-hidden="true" />
            Rename Files
          </Button>
        )}

        {/* Division quick-filter — jump straight to a CSI division */}
        {divisions.length > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              overflowX: 'auto',
              maxWidth: '52%',
              paddingBottom: 2,
            }}
          >
            <DivPill
              label="All"
              count={masters.length}
              on={!divFilter}
              onClick={() => setDivFilter(null)}
            />
            {divisions.map(([d, n]) => (
              <DivPill
                key={d}
                label={d}
                count={n}
                on={divFilter === d}
                onClick={() => setDivFilter(divFilter === d ? null : d)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Two-pane cockpit: section table (left) + live Adding cart (right) ── */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
        {/* Left — sections table */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: 'var(--space-4) var(--space-2) var(--space-1) var(--space-5)',
          }}
        >
          {loadError && (
            <div
              role="alert"
              style={{
                fontSize: 13,
                color: 'var(--dg)',
                padding: '0 0 var(--space-2)',
                flexShrink: 0,
              }}
            >
              {loadError}
            </div>
          )}
          {loading && masters.length === 0 ? (
            /* Loading skeleton — table silhouette while masters stream in */
            <div
              aria-hidden="true"
              className="animate-pulse"
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
            >
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: i === 0 ? 40 : 36,
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--sa)',
                  }}
                />
              ))}
            </div>
          ) : (
            <DataTable<SpecMaster>
              columns={columns}
              data={shownMasters}
              keyField="id"
              onRowClick={(m) => toggleMaster(m.id)}
              initialSort={{ key: 'csiNumber', direction: 'asc' }}
              searchPlaceholder="Search by section number or title…"
              storageKey="add-specs-masters"
              emptyIcon={'📚'}
              emptyTitle={divFilter ? 'No sections in this division' : 'No master specs'}
              emptyMessage={
                divFilter
                  ? 'Clear the division filter to see every section.'
                  : 'Create master specs in the Spec Masters library first.'
              }
              groupOptions={[
                {
                  key: 'division',
                  label: 'CSI Division',
                  getValue: (m) => {
                    const div = (m.csiNumber || '').split(' ')[0];
                    return div ? `Division ${div}` : 'Unnumbered';
                  },
                },
              ]}
              toolbarExtra={
                <Button
                  variant="outline"
                  onClick={() => {
                    const shownIds = new Set(shownMasters.map((m) => m.id));
                    const allShownSelected =
                      shownMasters.length > 0 && shownMasters.every((m) => isSelected(m.id));
                    if (allShownSelected) {
                      setSelectedMasters((prev) => prev.filter((s) => !shownIds.has(s.masterId)));
                    } else {
                      setSelectedMasters((prev) => {
                        const have = new Set(prev.map((s) => s.masterId));
                        return [
                          ...prev,
                          ...shownMasters
                            .filter((m) => !have.has(m.id))
                            .map((m) => ({ masterId: m.id })),
                        ];
                      });
                    }
                  }}
                >
                  {shownMasters.length > 0 && shownMasters.every((m) => isSelected(m.id))
                    ? divFilter
                      ? 'Clear division'
                      : 'Clear all'
                    : divFilter
                      ? 'Select division'
                      : 'Select all'}
                </Button>
              }
            />
          )}
        </div>

        {/* Right — live "Adding to project" cart (fills what used to be dead space) */}
        <aside
          style={{
            width: 360,
            flexShrink: 0,
            borderLeft: '1px solid var(--bd)',
            background: 'var(--sf)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {/* Cart header */}
          <div
            style={{
              padding: 'var(--space-4) var(--space-4) var(--space-3)',
              borderBottom: '1px solid var(--bd)',
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'var(--tm)',
              }}
            >
              Adding to project
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 'var(--space-2)',
                marginTop: 'var(--space-1)',
              }}
            >
              <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.25 }}>
                {selectedMasters.length}
              </span>
              <span style={{ fontSize: 13, color: 'var(--tm)' }}>
                section{selectedMasters.length === 1 ? '' : 's'}
                {newCount > 0 && newCount !== selectedMasters.length ? ` · ${newCount} new` : ''}
              </span>
            </div>
          </div>

          {/* Forma — belongs with "what am I adding" */}
          <div
            style={{
              padding: 'var(--space-3) var(--space-4)',
              borderBottom: '1px solid var(--bd)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-2)',
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)' }}>Forma</span>
              {formaIsClientMatch && (
                <span style={badge('rgba(var(--acr),0.1)', 'var(--ac)')}>Client match</span>
              )}
            </div>
            <SearchableSelect
              value={selectedFormaId}
              options={formaOptions}
              onChange={(v) => setSelectedFormaId(v || '')}
              clearable
              searchPlaceholder="Search formas…"
              placeholder={standards.length === 0 ? 'No formas' : 'Choose a forma…'}
            />
          </div>

          {/* Cart body — selected sections + live smart-block resolution */}
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: 'var(--space-3) var(--space-4)',
              minHeight: 0,
            }}
          >
            {selectedMasters.length === 0 ? (
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  color: 'var(--tm)',
                  gap: 'var(--space-3)',
                  padding: '0 var(--space-3)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-lg)',
                    background: 'var(--sa)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Zap size={18} strokeWidth={2} />
                </span>
                <div style={{ fontSize: 13, lineHeight: 1.5, maxWidth: 240 }}>
                  Pick sections on the left — they stack up here, and any{' '}
                  <strong style={{ color: 'var(--tx)' }}>smart blocks resolve live</strong> from
                  this project before you ever add.
                </div>
              </div>
            ) : (
              <>
                {/* Selected sections list */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-1)',
                    marginBottom: 'var(--space-4)',
                  }}
                >
                  {selectedMasters.map((sel) => {
                    const m = masters.find((x) => x.id === sel.masterId);
                    if (!m) return null;
                    const variantName = sel.variantId
                      ? (m.variants || []).find((v) => v.id === sel.variantId)?.name
                      : undefined;
                    const wasThere = existingSpecs.some((s) => s.masterId === m.id);
                    return (
                      <div
                        key={sel.masterId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2)',
                          borderRadius: 'var(--radius-md)',
                          background: 'var(--bg)',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 11,
                            color: 'var(--ac)',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {m.csiNumber}
                        </span>
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 13,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={m.title}
                        >
                          {m.title}
                        </span>
                        {variantName && (
                          <span
                            style={badge(
                              'color-mix(in srgb, var(--color-warning) 15%, transparent)',
                              'var(--color-warning)'
                            )}
                          >
                            {variantName}
                          </span>
                        )}
                        {wasThere && (
                          <span style={{ fontSize: 10, color: 'var(--tm)', flexShrink: 0 }}>
                            in project
                          </span>
                        )}
                        <button
                          onClick={() => toggleMaster(m.id)}
                          title="Remove"
                          aria-label={`Remove ${m.csiNumber || m.title} from selection`}
                          style={{
                            width: 32,
                            height: 32,
                            margin: '-8px -8px -8px 0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--tm)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            padding: 0,
                            flexShrink: 0,
                          }}
                        >
                          <X size={14} strokeWidth={2} aria-hidden="true" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Live smart-block resolution */}
                {smartBlockCount > 0 && (
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        marginBottom: 'var(--space-2)',
                        color: SMART_ACCENT,
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                      }}
                    >
                      <Zap size={12} strokeWidth={2.5} aria-hidden="true" />
                      {smartBlockCount} smart block{smartBlockCount === 1 ? '' : 's'} resolving live
                    </div>
                    {!resolveCtx ? (
                      <div
                        style={{ fontSize: 11, color: 'var(--tm)', padding: 'var(--space-1) 0' }}
                      >
                        Reading project context…
                      </div>
                    ) : liveSections.length === 0 ? (
                      <div
                        style={{ fontSize: 11, color: 'var(--tm)', padding: 'var(--space-1) 0' }}
                      >
                        These resolve once the project has matching context.
                      </div>
                    ) : (
                      liveSections.map((sec) => (
                        <div key={sec.master.id} style={{ marginBottom: 'var(--space-3)' }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-2)',
                              marginBottom: 'var(--space-1)',
                            }}
                          >
                            <span
                              style={{
                                fontFamily: 'var(--font-mono)',
                                fontSize: 11,
                                color: 'var(--ac)',
                                fontWeight: 700,
                              }}
                            >
                              {sec.master.csiNumber}
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                color: 'var(--tm)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {sec.master.title}
                            </span>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 'var(--space-2)',
                            }}
                          >
                            {sec.tokens.map((t, i) => (
                              <TokenPreviewCard key={(t.block as any).id || i} token={t} />
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Cart footer — single primary action. Hand-rolled (not kit Button) so
              the guidance title still shows on hover while disabled. */}
          <div
            style={{ padding: 'var(--space-3) var(--space-4)', borderTop: '1px solid var(--bd)' }}
          >
            <button
              onClick={() => void addToProject()}
              disabled={selectedMasters.length === 0 || adding}
              title={
                selectedMasters.length === 0
                  ? 'Select at least one section'
                  : `Add ${selectedMasters.length} section${selectedMasters.length === 1 ? '' : 's'} to ${activeProject.name} and open the project`
              }
              style={{
                width: '100%',
                padding: 'var(--space-3) var(--space-5)',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: selectedMasters.length > 0 && !adding ? 'var(--ac)' : 'var(--sa)',
                color:
                  selectedMasters.length > 0 && !adding ? 'var(--color-text-inverse)' : 'var(--tm)',
                fontSize: 14,
                fontWeight: 700,
                cursor: selectedMasters.length > 0 && !adding ? 'pointer' : 'default',
                transition: FAST_COLORS,
              }}
            >
              {adding
                ? 'Adding…'
                : selectedMasters.length > 0
                  ? `Add ${selectedMasters.length} to ${activeProject.name?.split(' ')[0] || 'Project'}`
                  : 'Add to Project'}
            </button>
          </div>
        </aside>
      </div>
      {showRenameModal && renameModal}
    </div>
  );
}

// ─── Token preview card — one smart block's resolved output ───
function TokenPreviewCard({
  token,
}: {
  token: { block: ContentBlock; sb: SmartBlock | undefined; resolved: ContentBlock[] };
}) {
  const { block, sb, resolved } = token;
  const name = sb?.name ?? (block as any).smartBlockKind ?? 'Smart Block';
  const accent = sb?.color ?? SMART_ACCENT;
  const iconName = sb?.icon ?? 'Sparkles';
  // @ts-ignore — dynamic lucide lookup, same pattern as SmartTokenResolvedView
  const Icon = (LucideIcons[iconName] as any) ?? LucideIcons.Sparkles;

  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--bd)',
        borderLeft: `3px solid ${accent}`,
        background: 'var(--sf)',
        padding: 'var(--space-3)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-2)',
          color: accent,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
        }}
      >
        <Icon size={11} strokeWidth={2.5} aria-hidden="true" />
        <span>Smart · {name}</span>
      </div>
      {resolved.length === 0 ? (
        <span style={{ fontSize: 13, fontStyle: 'italic', color: 'var(--tm)' }}>
          (no content from current project — resolves once the project has matching context)
        </span>
      ) : (
        <ResolvedBlocks blocks={resolved} />
      )}
    </div>
  );
}

// ─── Division quick-filter pill ───
function DivPill({
  label,
  count,
  on,
  onClick,
}: {
  label: string;
  count: number;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={on}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-full)',
        border: '1px solid ' + (on ? 'var(--ac)' : 'var(--bd)'),
        background: on ? 'var(--ac)' : 'transparent',
        color: on ? 'var(--color-text-inverse)' : 'var(--tm)',
        fontSize: 11,
        fontWeight: 600,
        fontFamily: label === 'All' ? 'inherit' : 'var(--font-mono)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        transition: FAST_COLORS,
      }}
    >
      {label}
      <span style={{ opacity: 0.7, fontWeight: 500 }}>{count}</span>
    </button>
  );
}

// ─── Shared styles ───
function badge(bg: string, color: string): React.CSSProperties {
  return {
    fontSize: 11,
    padding: '2px var(--space-2)',
    borderRadius: 'var(--radius-sm)',
    background: bg,
    color,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(0, 0, 0, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: 'var(--sf)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  width: 560,
  border: '1px solid var(--bd)',
  boxShadow: 'var(--shadow-xl)',
  maxHeight: '80vh',
  overflow: 'auto',
};

(window as any).AddSpecs = AddSpecs;
export { AddSpecs };
